// Default values + template metadata for Billing Settings.
//
// These are the only source of the platform defaults — there is no second
// surface to keep in sync. (An earlier draft mirrored a Nova LiveView and
// an Elixir `LateFeePolicy.default/0`; both were rejected with ws-common
// #6331 and never landed.)
//
// Every value here is evidence-backed — see PAYM-5075:
// https://linear.app/wonderschool/issue/PAYM-5075/provider-late-payment-policy-setup
// 70% of late payers self-cure within 5 days, which is why grace is 5.

import type { BillingSettingsData, EmailTemplate, LateFeePolicy } from './types';

export const DEFAULT_LATE_FEE_POLICY: LateFeePolicy = {
  policyEnabled: false, // schools opt IN; this is not a default-on policy
  lateFeeType: 'flat',
  lateFeeAmount: 25,
  maxLateFee: 100,
  gracePeriodDays: 5,
  graceAmountThreshold: 25, // decided over 10: a $25 fee on a $10 debt is a 250% penalty
  feeScope: 'per_invoice',
  approvalMode: 'review_queue', // safer default than auto-apply
  subsidyInvoicesExempt: true, // CA + other states restrict this
  // PAYM-5076's enforcement sweep will read this. Keeping it in the stored
  // shape also makes removing the UI toggle reversible if providers ask for it.
  remindersEnabled: true,
  maxConsecutiveMonths: 3,
  reminderDaysBefore: 3,
  firmReminderDaysAfter: 7,
  escalationDaysAfter: 14,
};

// Code-owned copy for the dunning ladder. NOT provider-editable and NOT stored
// in Firestore — providers set only which days reminders send.
// Consumed by the enforcement sweep in PAYM-5076.
export const DEFAULT_EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    key: 'reminder_due_soon',
    subject: 'Reminder: [invoice_amount] due [due_date]',
    body:
      'Hi [parent_name],\n\nThis is a friendly reminder that your invoice of [invoice_amount] for ' +
      '[child_name] is due on [due_date].\n\nPay here: [payment_link]\n\nThanks!\n[school_name]',
  },
  {
    key: 'reminder_due_today',
    subject: 'Your invoice of [invoice_amount] is due today',
    body:
      'Hi [parent_name],\n\nYour invoice of [invoice_amount] for [child_name] is due today.\n\n' +
      'Pay here: [payment_link]\n\nThanks!\n[school_name]',
  },
  {
    key: 'reminder_past_due',
    subject: 'Past due: [invoice_amount] for [child_name]',
    body:
      'Hi [parent_name],\n\nYour invoice of [invoice_amount] for [child_name] was due on [due_date] ' +
      "and is now [days_past_due] day(s) past due. We haven't applied a late fee yet.\n\n" +
      'Pay here: [payment_link]\n\nThanks,\n[school_name]',
  },
  {
    key: 'late_fee_applied',
    subject: 'A late fee of [late_fee_amount] was added to your account',
    body:
      'Hi [parent_name],\n\nBecause the invoice for [child_name] was past the grace period, a late ' +
      'fee of [late_fee_amount] has been added. Your total now owed is [total_now_owed].\n\n' +
      'Pay here: [payment_link]\n\nIf you think this is a mistake please reach out to ' +
      '[director_name].\n\n[school_name]',
  },
  {
    key: 'firm_reminder',
    subject: 'Action needed: [total_now_owed] past due',
    body:
      'Hi [parent_name],\n\nYour balance for [child_name] is now [total_now_owed] and is ' +
      '[days_past_due] day(s) past due. Please bring the account current to avoid further fees.\n\n' +
      'Pay here: [payment_link]\n\nIf you need to set up a payment plan, please contact ' +
      '[director_name] at [school_name].',
  },
  {
    key: 'escalation',
    subject: 'Final notice — account [days_past_due] days past due',
    body:
      'Hi [parent_name],\n\nYour account for [child_name] is now [days_past_due] day(s) past due ' +
      'with a balance of [total_now_owed]. We need to hear from you to keep the enrollment in ' +
      'good standing.\n\nPay here: [payment_link]\n\nPlease contact [director_name] at ' +
      '[school_name] today.',
  },
];

export const DEFAULT_BILLING_SETTINGS: BillingSettingsData = {
  lateFeePolicy: DEFAULT_LATE_FEE_POLICY,
};
